/**
 * UpgradeProvider — provider global do toolkit de gating. Renderiza um UpgradeSheet
 * (MUI Dialog) acionado por (a) gating proativo no front (useGuardedAction) e
 * (b) interceptor 402 do axios (via upgradeBus). CTA de assinatura fica "Em breve"
 * (disabled) neste escopo — o Billing (Escopo 2) só troca isso por checkout real.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, Stack,
} from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useNavigate } from 'react-router-dom';
import { setUpgradeHandler } from '../../services/upgradeBus';

const PLAN_NAMES = { free: 'Grátis', essencial: 'Essencial', pro: 'Pro', business: 'Business' };

const UpgradeContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useUpgrade() {
  return useContext(UpgradeContext);
}

export default function UpgradeProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState(null);
  const navigate = useNavigate();

  const openUpgrade = useCallback((context) => {
    setCtx(context || {});
    setOpen(true);
  }, []);

  const close = () => setOpen(false);

  // Registra o handler para o interceptor 402 (fora do React) poder abrir o sheet.
  useEffect(() => {
    setUpgradeHandler((context) => openUpgrade(context));
    return () => setUpgradeHandler(null);
  }, [openUpgrade]);

  const suggested = ctx?.suggestedPlanSlug;
  const isFeature = ctx?.error === 'feature_locked';
  const isQuota = ctx?.error === 'quota_exceeded';
  const title = isQuota
    ? 'Saldo insuficiente'
    : isFeature ? 'Recurso indisponível no seu plano' : 'Limite do plano atingido';

  const goToPlans = () => { setOpen(false); navigate('/plans'); };

  return (
    <UpgradeContext.Provider value={{ openUpgrade }}>
      {children}
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkspacePremiumIcon color="primary" fontSize="small" />
          {title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {ctx?.message || 'Você atingiu um limite do seu plano atual.'}
          </Typography>
          {suggested && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Disponível a partir do plano</Typography>
              <Chip color="primary" size="small" label={PLAN_NAMES[suggested] || suggested} />
            </Stack>
          )}
          {isQuota && Array.isArray(ctx?.suggestedPacks) && ctx.suggestedPacks.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">Pacotes disponíveis:</Typography>
              {ctx.suggestedPacks.map((p) => <Chip key={p} size="small" variant="outlined" label={p} />)}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Agora não</Button>
          <Button variant="contained" onClick={goToPlans}>{isQuota ? 'Ver pacotes' : 'Ver planos'}</Button>
        </DialogActions>
      </Dialog>
    </UpgradeContext.Provider>
  );
}
